import type { DraftData } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { useMemo, useState } from 'react';
import type { AddAttributeFunction } from './state.mapping';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { AttributeReader } from './attributeReader.types';
import { addError } from '@shared/utils/logger';

export type CardGeneralData = DraftData['config']['customizations'];

export type ProductAttributeState = {
  productAttributeId: string;
  getOptionId: (draftData: DraftJson) => string;
  setOptionId: (newState: string) => void;
  allowedOptionIDs: string[];
};

type StateReducerAction =
  | {
      task: 'registerAttribute';
      newAttribute: ProductAttributeState;
    }
  | {
      task: 'removeAttribute';
      attributeId: string;
    };

const stateReducer = (state: ProductAttributeState[], action: StateReducerAction): ProductAttributeState[] => {
  switch (action.task) {
    case 'registerAttribute': {
      const replaceStep = state.filter(attribute => attribute.productAttributeId !== action.newAttribute.productAttributeId);
      return [...replaceStep, action.newAttribute];
    }
    case 'removeAttribute': {
      return state.filter(attribute => attribute.productAttributeId !== action.attributeId);
    }
    default: {
      throw new Error('Unknown StateReducer task');
    }
  }
};

const getValidatedValue = (value: string, options: string[]): string => {
  if (!options.includes(value)) {
    return options[0];
  }

  return value;
};

const READ_FAILED = Symbol('readFailed');
// Reads run every render; report each failing reader once.
const reportedReaders = new WeakSet<AttributeReader>();

const readInitialOptionId = (reader: AttributeReader, draftData: DraftJson): string | typeof READ_FAILED => {
  try {
    return getValidatedValue(reader.getOptionId(draftData), reader.allowedOptionIDs);
  } catch (error) {
    if (!reportedReaders.has(reader)) {
      reportedReaders.add(reader);
      addError(error, { context: 'InitialProductAttributeReadFailed', attributeId: reader.productAttributeId });
    }
    return READ_FAILED;
  }
};

/** `initialAttributes` answer reads until the first registration or product switch. They never write. */
export const useProductAttributeStates = (initialAttributes?: readonly AttributeReader[]) => {
  const [attributeStates, setAttributeStates] = useState<ProductAttributeState[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const initialStates = useMemo(
    () =>
      initialAttributes?.map(reader => ({
        ...reader,
        getOptionId: (draftData: DraftJson) => {
          const optionId = readInitialOptionId(reader, draftData);
          return optionId === READ_FAILED ? '' : optionId;
        },
        setOptionId: () => {},
      })),
    [initialAttributes],
  );
  const isReadingInitial = isInitialLoad && !!initialAttributes;
  const readableStates = isReadingInitial && initialStates ? initialStates : attributeStates;

  // Use the StateReducer
  const runStateReducer = (action: StateReducerAction) => {
    setAttributeStates(old => {
      return stateReducer(old, action);
    });
  };

  /**
   * Add an Attribute to the ProductAttributeStates
   */
  const addAttribute: AddAttributeFunction = (newAttribute: ProductAttributeState, product: StationeryProduct) => {
    // Create validation wrappers for getters and setters.
    // Forces optionId to conform to allowed optionIDs for product.
    // Safely handles edge case where draft is using an option,
    // and then that option is removed. Without this, we would try and
    // generate a price for that option, would would cause an error in
    // PrintPriceCalculator

    setIsInitialLoad(false);

    const productAttributes = product.attributes.map(a => a.attributeId);
    if (!productAttributes.includes(newAttribute.productAttributeId)) {
      return;
    }

    const validateGetOptionId = (draftData: DraftJson) => {
      const optionId = newAttribute.getOptionId(draftData);
      const validatedOptionId = getValidatedValue(optionId, newAttribute.allowedOptionIDs);
      if (optionId !== validatedOptionId) {
        newAttribute.setOptionId(validatedOptionId);
      }

      return validatedOptionId;
    };

    const validateSetOptionId = (newState: string) => {
      const optionId = getValidatedValue(newState, newAttribute.allowedOptionIDs);
      newAttribute.setOptionId(optionId);
    };

    runStateReducer({
      task: 'registerAttribute',
      newAttribute: {
        ...newAttribute,
        getOptionId: validateGetOptionId,
        setOptionId: validateSetOptionId,
      },
    });
  };

  /**
   * Get an Attribute from ProductAttributeStates.
   * @returns Attribute, including getter and setter for it's data.
   */
  const getAttribute = (attributeId: ProductAttributeState['productAttributeId']) => {
    return readableStates.find(attr => attr.productAttributeId === attributeId);
  };

  const toFullState = (states: ProductAttributeState[], draftData: DraftJson): { attributeId: string; optionId: string }[] => {
    return states.map(state => {
      return {
        attributeId: state.productAttributeId,
        optionId: state.getOptionId(draftData),
      };
    });
  };

  const getFullState = (draftData: DraftJson): { attributeId: string; optionId: string }[] => {
    if (!isReadingInitial || !initialAttributes) {
      return toFullState(attributeStates, draftData);
    }
    const optionIds = initialAttributes.map(reader => readInitialOptionId(reader, draftData));
    if (optionIds.some(optionId => optionId === READ_FAILED)) {
      return [];
    }
    return initialAttributes.map((reader, index) => ({ attributeId: reader.productAttributeId, optionId: optionIds[index] as string }));
  };

  /** Ignores `initialAttributes`; the draft's metadata is written from this. */
  const getRegisteredFullState = (draftData: DraftJson) => toFullState(attributeStates, draftData);

  const destroyLinks = () => {
    setIsInitialLoad(false);
    setAttributeStates([]);
  };

  return {
    destroyLinks,
    addAttribute,
    getAttribute,
    getFullState,
    getRegisteredFullState,
  };
};
